import React from "react";
import { useHistory } from "react-router";
import DashboardIcon from "@atlaskit/icon/glyph/dashboard";
import ChevronUpIcon from "@atlaskit/icon/glyph/chevron-up";
import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import FolderIcon from "@atlaskit/icon/glyph/folder";
import EmailIcon from "@atlaskit/icon/glyph/email";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { Logo } from "../Logo";
import { Version } from "../Version";
import useAuth from "../../routes/login/AuthContext";
import { HOME_URL } from "../../routes/HomePage/HomePage";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { handleNavigation } from "../../helpers/handleNavigation";

import { SubNav } from "./SubNav";
import { useRouteSelectedHandler } from "./useRouteSelectedHandler";
import { ListItemText } from "../../UI/ListItemText";

import { DrawerHeader, StyledDrawer, StyledListItemButton } from "./styles";
import { SUBNAV } from "./types";
import { useHeaderContext } from "../Header/context";
import { List } from "../../UI/List";
import { ListItemIcon } from "../../UI/ListItemIcon";
import { COLOR_SCIENCE_BLUE, COLOR_WHITE } from "../../constants/styles";
import { Box } from "../../UI/Box";
import {
  getContractUrl,
  getResourcesUrl,
  READ_SUPER_ADMIN_SUBNAV_LINKS,
  SETTINGS_SUBNAV_LINKS,
  SUPER_ADMIN_SUBNAV_LINKS,
} from "../../constants/navigation";
import { HOW_TO_PAGE_URL } from "../../routes/HowToPage";
import { LogoutListItem } from "./LogoutListItem";
import { HeaderSpacing } from "../HeaderSpacing";
import { useNetwork } from "../../hooks/useNetwork";
import { ReportsItem } from "./ReportsItem";
import { UsersItem } from "./UsersItem";
import { IconButton } from "../../UI/IconButton";
import { HowToIcon } from "./HowToIcon";
import { SettingsIcon } from "./SettingsIcon";
import { SuperAdminIcon } from "./SuperAdminIcon";

const getExpandLink = (open: boolean) =>
  open ? (
    <ChevronUpIcon label="Chevron up" />
  ) : (
    <ChevronDownIcon label="Chevron down" />
  );

const getDrawerVariant = (isUpMd: boolean) =>
  isUpMd ? "permanent" : undefined;

const getIconColor = (selected: boolean) =>
  selected ? COLOR_SCIENCE_BLUE : undefined;

export const Nav: React.FC = () => {
  const { push } = useHistory();
  const { isSuperAdmin, isSuperAdminWrite, isSuperAdminRead } = useAuth();
  const { isUpMd } = useMediaQuery();
  const { selectedNetwork } = useNetwork();
  const { closeNav, isNavOpen, openNav } = useHeaderContext();
  const [subnavOpen, setSubnavOpen] = React.useState<SUBNAV>();
  const { isHomePage } = useRouteSelectedHandler(setSubnavOpen);

  React.useEffect(() => {
    if (isNavOpen) {
      return;
    }

    setSubnavOpen(undefined);
  }, [isNavOpen]);

  const handleNavClick = (url: string) => (event: React.MouseEvent) => {
    handleNavigation(event, url, push);

    if (!isUpMd) {
      closeNav();
    }
  };

  const handleResourcesClick = () => {
    window.open(getResourcesUrl(selectedNetwork.apt));
  };

  const handleContactClick = () => {
    window.open(getContractUrl());
  };

  const toogleSubnav = (subnav: SUBNAV) => () => {
    setSubnavOpen((prevSubnav) => (prevSubnav === subnav ? undefined : subnav));

    if (!isNavOpen) {
      openNav();
    }
  };

  const openNavListSx = isNavOpen && {
    overflowY: "auto",
    overflowX: "hidden",
  };

  return (
    <StyledDrawer
      open={isNavOpen}
      onClose={closeNav}
      variant={getDrawerVariant(isUpMd)}
    >
      {isUpMd && <HeaderSpacing />}
      <DrawerHeader>
        <IconButton onClick={closeNav}>
          <ChevronLeftIcon htmlColor={COLOR_WHITE} />
        </IconButton>
        <Logo type="white" marginTop="0" marginBottom="0" />
      </DrawerHeader>
      <List sx={{ pt: 2, ...openNavListSx }}>
        <li>
          <StyledListItemButton
            selected={isHomePage}
            onClick={handleNavClick(HOME_URL)}
          >
            <ListItemIcon>
              <DashboardIcon
                label="Dashboard"
                primaryColor={getIconColor(isHomePage)}
              />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </StyledListItemButton>
        </li>
        <li>
          <UsersItem
            subnavOpen={subnavOpen}
            toogleSubnav={toogleSubnav(SUBNAV.USERS)}
          />
        </li>
        <li>
          <ReportsItem
            subnavOpen={subnavOpen}
            toogleSubnav={toogleSubnav(SUBNAV.REPORTS)}
          />
        </li>
        <li>
          <StyledListItemButton onClick={toogleSubnav(SUBNAV.SETTINGS)}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
            {getExpandLink(subnavOpen === SUBNAV.SETTINGS)}
          </StyledListItemButton>
          <SubNav
            in={subnavOpen === SUBNAV.SETTINGS}
            links={SETTINGS_SUBNAV_LINKS}
          />
        </li>
        {isSuperAdmin() && isSuperAdminWrite() && (
          <li>
            <StyledListItemButton onClick={toogleSubnav(SUBNAV.SUPER_ADMIN)}>
              <ListItemIcon>
                <SuperAdminIcon />
              </ListItemIcon>
              <ListItemText primary="Super Admin" />
              {getExpandLink(subnavOpen === SUBNAV.SUPER_ADMIN)}
            </StyledListItemButton>
            <SubNav
              in={subnavOpen === SUBNAV.SUPER_ADMIN}
              links={SUPER_ADMIN_SUBNAV_LINKS}
            />
          </li>
        )}
        {isSuperAdmin() && isSuperAdminRead() && (
          <li>
            <StyledListItemButton onClick={toogleSubnav(SUBNAV.SUPER_ADMIN)}>
              <ListItemIcon>
                <SuperAdminIcon />
              </ListItemIcon>
              <ListItemText primary="Super Admin" />
              {getExpandLink(subnavOpen === SUBNAV.SUPER_ADMIN)}
            </StyledListItemButton>
            <SubNav
              in={subnavOpen === SUBNAV.SUPER_ADMIN}
              links={READ_SUPER_ADMIN_SUBNAV_LINKS}
            />
          </li>
        )}
        <li>
          <StyledListItemButton onClick={handleNavClick(HOW_TO_PAGE_URL)}>
            <ListItemIcon>
              <HowToIcon />
            </ListItemIcon>
            <ListItemText primary="How To" />
          </StyledListItemButton>
        </li>
        <li>
          <StyledListItemButton onClick={handleResourcesClick}>
            <ListItemIcon>
              <FolderIcon label="Resources" />
            </ListItemIcon>
            <ListItemText primary="Resources" />
          </StyledListItemButton>
        </li>
        <li>
          <StyledListItemButton onClick={handleContactClick}>
            <ListItemIcon>
              <EmailIcon label="Contact Us" />
            </ListItemIcon>
            <ListItemText primary="Contact Us" />
          </StyledListItemButton>
        </li>
        <li>
          <LogoutListItem />
        </li>
      </List>
      <Box mt="auto" ml={2} mb={1}>
        <Version />
      </Box>
    </StyledDrawer>
  );
};
